import { useState, useContext } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const usePayments = () => {
  const { api, userID } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [settlementCharges, setSettlementCharges] = useState([]);
  const [orderTypeCount, setOrderTypeCount] = useState([]);
  const [settlementInvoices, setSettlementInvoices] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [transactionLogs, setTransactionLogs] = useState([]);
  const [transactionsReport, setTransactionsReport] = useState([]);
  const [transactionsReportLoading, setTransactionsReportLoading] =
    useState(false);

  // Transactions Report
  const getTransactions = async (fromDate, toDate) => {
    try {
      setLoading(true);

      const res = await api.post("/Transactions/getTransactionsReport", {
        userid: userID,
        fromDate,
        toDate,
      });

      if (res.data?.responseCode === "00") {
        setTransactions(res.data.response || []);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      console.error("Transactions Error:", err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Analytics
  const getAnalytics = async (fromDate, toDate) => {
    try {
      setLoading(true);

      const res = await api.post(
        "/Transactions/v1.0/getTransactionsAnalytics",
        { userid: userID, fromDate, toDate },
      );

      if (res.data?.responseCode === "00") {
        setAnalytics(res.data.response);
      } else {
        setAnalytics(null);
      }
    } catch (err) {
      console.error("Analytics Error:", err);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  // Processed Settlements
  const getProcessedSettlements = async (fromDate, toDate) => {
    try {
      setLoading(true);

      const res = await api.post("/Transactions/v1.0/getProcessedSettlements", {
        userid: userID,
        fromDate,
        toDate,
      });

      if (res.data?.responseCode === "00") {
        setSettlements(res.data.response || []);
      } else {
        setSettlements([]);
      }
    } catch (err) {
      console.error("Settlements Error:", err);
      setSettlements([]);
    } finally {
      setLoading(false);
    }
  };

  // Refund Report
  const getRefunds = async () => {
    try {
      setLoading(true);

      const res = await api.post("/Transactions/v1.0/getRefundReport", {
        userid: userID,
      });

      if (res.data?.responseCode === "00") {
        setRefunds(res.data.response || []);
      } else {
        setRefunds([]);
      }
    } catch (err) {
      console.error("Refund Error:", err);
      setRefunds([]);
    } finally {
      setLoading(false);
    }
  };

  // Settlement Charges
  const getSettlementCharges = async (fromDate, toDate) => {
    try {
      setLoading(true);

      const res = await api.post("/Order/v1.0/getSettlementCharges", {
        userid: userID,
        fromDate,
        toDate,
      });

      if (res.data?.responseCode === "00") {
        setSettlementCharges(res.data.response || []);
      } else {
        setSettlementCharges([]);
      }
    } catch (err) {
      console.error("Settlement Charges Error:", err);
      setSettlementCharges([]);
    } finally {
      setLoading(false);
    }
  };

  // Order Type Count
  const getOrderTypeCount = async (fromDate, toDate) => {
    try {
      setLoading(true);

      const res = await api.post("/Admin/v2.0/getClientOrderSummaryDetails", {
        userid: userID,
        fromDate,
        toDate,
      });

      if (res.data?.responseCode === "00") {
        setOrderTypeCount(res.data.response || []);
      } else {
        setOrderTypeCount([]);
      }
    } catch (err) {
      console.error("Order Type Error:", err);
      setOrderTypeCount([]);
    } finally {
      setLoading(false);
    }
  };

  // Settlement Invoices (GET)
  const getSettlementInvoices = async () => {
    try {
      setLoading(true);

      const res = await api.post("/Order/v1.0/getSettlementInvoices", {
        userid: userID,
      });

      if (res.data?.responseCode === "00") {
        setSettlementInvoices(res.data.response || []);
      } else {
        setSettlementInvoices([]);
      }
    } catch (err) {
      console.error("Invoices Error:", err);
      setSettlementInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // Generate Settlement Invoices
  const generateSettlementInvoices = async (fromDate, toDate) => {
    try {
      setLoading(true);

      const res = await api.post("/Order/v1.0/generateSettlementInvoices", {
        userid: userID,
        fromDate,
        toDate,
      });

      return res.data;
    } catch (err) {
      console.error("Generate Invoice Error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getWalletTransactionDetails = async (fromDate, toDate) => {
    try {
      setLoading(true);

      const res = await api.post("/Transactions/getWalletTransactionDetails", {
        userid: userID,
        fromDate,
        toDate,
      });

      if (res.data?.responseCode === "00") {
        setWalletTransactions(res.data.response || []);
      } else {
        setWalletTransactions([]);
      }
    } catch (err) {
      console.error("Wallet Transaction Error:", err);
      setWalletTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionLogs = async ({
    fromDate,
    toDate,
    paymentMode = "",
    rrn = "",
  }) => {
    try {
      setLoading(true);

      const res = await api.post(
        "/Transactions/v1.0/getTransactionLogsV1",
        {
          userId: userID,
          fromDate,
          toDate,
          paymentMode,
          rrn,
        },
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );

      if (res.data?.responseCode === "00") {
        setTransactionLogs(res.data.response || []);
        return res.data.response || [];
      }

      setTransactionLogs([]);
      return [];
    } catch (err) {
      console.error("Transaction Logs Error:", err);
      setTransactionLogs([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getTransactionsReport = async (payload) => {
    try {
      setTransactionsReportLoading(true);

      const res = await api.post(
        "/Transactions/v1.0/getTransactionsReport",
        payload,
      );

      if (res.data?.responseCode === "00") {
        const responseData = Array.isArray(res.data?.response)
          ? res.data.response
          : res.data?.response?.data || res.data?.response?.transactions || [];

        setTransactionsReport(responseData);

        return {
          ...res.data,
          response: responseData,
        };
      }

      setTransactionsReport([]);
      return res.data;
    } catch (err) {
      console.error(
        "Get Transactions Report Error:",
        err?.response?.data || err,
      );

      setTransactionsReport([]);

      return {
        responseCode: "01",
        responseMessage:
          err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch transactions report",
        response: [],
      };
    } finally {
      setTransactionsReportLoading(false);
    }
  };

  // ================= RETURN =================

  return {
    loading,

    wallet,
    transactions,
    analytics,
    settlements,
    refunds,
    settlementCharges,
    orderTypeCount,
    settlementInvoices,
    walletTransactions,
    transactionLogs,
    transactionsReport,
    transactionsReportLoading,
    getTransactions,
    getAnalytics,
    getProcessedSettlements,
    getRefunds,
    getSettlementCharges,
    getOrderTypeCount,
    getSettlementInvoices,
    generateSettlementInvoices,
    getWalletTransactionDetails,
    getTransactionLogs,

    getTransactionsReport,
  };
};

export default usePayments;



